import React, { useState, useEffect } from "react";
import PaymentMethodsTable from "./PaymentMethodsTable";
import PaymentMethodForm from "./PaymentMethodForm";
import ConfirmationModal from "./ConfirmationModal";
import PaymentMethodService from "../../../service/PaymentMethodService";

function PaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const paymentMethodService = new PaymentMethodService();

  // Fetch payment methods from API using service
  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      
      const methods = await paymentMethodService.fetchPaymentMethods({ activeOnly: false });
      setPaymentMethods(methods);
      
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setErrorMessage(error.message || "Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  // Create new payment method
  const handleCreate = async (formData) => {
    try {
      setErrorMessage("");
      
      await paymentMethodService.createPaymentMethod(formData);
      setSuccessMessage('Payment method created successfully!');
      setShowForm(false);
      fetchPaymentMethods();
      setTimeout(() => setSuccessMessage(""), 3000);
      
    } catch (error) {
      console.error('Error creating payment method:', error);
      setErrorMessage(error.message || "Failed to create payment method");
    }
  };

  // Update payment method
  const handleUpdate = async (formData) => {
    try {
      setErrorMessage("");
      
      await paymentMethodService.updatePaymentMethod(editingMethod._id, formData);
      setSuccessMessage('Payment method updated successfully!');
      setShowForm(false);
      setEditingMethod(null);
      fetchPaymentMethods();
      setTimeout(() => setSuccessMessage(""), 3000);
      
    } catch (error) {
      console.error('Error updating payment method:', error);
      setErrorMessage(error.message || "Failed to update payment method");
    }
  };

  // Delete payment method (soft delete)
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setErrorMessage("");
      
      await paymentMethodService.deletePaymentMethod(deleteConfirm._id);
      setSuccessMessage('Payment method deleted successfully!');
      setDeleteConfirm(null);
      fetchPaymentMethods();
      setTimeout(() => setSuccessMessage(""), 3000);
      
    } catch (error) {
      console.error('Error deleting payment method:', error);
      setErrorMessage(error.message || "Failed to delete payment method");
    }
  };

  // Toggle active status
  const handleToggleStatus = async (method) => {
    try {
      setErrorMessage("");
      
      await paymentMethodService.togglePaymentMethodStatus(method._id);
      setSuccessMessage(`Payment method ${method.isActive ? 'deactivated' : 'activated'} successfully!`);
      fetchPaymentMethods();
      setTimeout(() => setSuccessMessage(""), 3000);
      
    } catch (error) {
      console.error('Error toggling payment method status:', error);
      setErrorMessage(error.message || "Failed to toggle payment method status");
    }
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingMethod(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingMethod(null);
    setErrorMessage("");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="text-gray-800 md:px-5 md:py-4 p-2 bg-white max-w-screen-lg mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="md:text-[26px] text-[20px] font-semibold">
          Payment Methods Management
        </h1>
        <button
          onClick={handleAddNew}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
        >
          + Add New Method
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          {errorMessage}
          <button 
            onClick={fetchPaymentMethods}
            className="ml-4 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
          >
            Retry
          </button>
        </div>
      )}

      {/* Main Content */}
      {showForm ? (
        <PaymentMethodForm
          method={editingMethod}
          onSubmit={editingMethod ? handleUpdate : handleCreate}
          onCancel={handleFormClose}
        />
      ) : (
        <PaymentMethodsTable
          paymentMethods={paymentMethods}
          onEdit={handleEdit}
          onDelete={setDeleteConfirm}
          onToggleStatus={handleToggleStatus}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirm}
        title="Delete Payment Method"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This will make it inactive.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
        type="delete"
      />
    </div>
  );
}

export default PaymentMethods;